/**
 * Meta Pixel, loaded lazily so its ~220 KB never competes with the hero image.
 * `initMetaPixel` is called after the first interaction (or a short timeout) and
 * fires PageView once; `trackPixelEvent` queues events until the script is ready.
 */
export const META_PIXEL_ID = '1683149889416153'

type PixelFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[][]
  push?: unknown
  loaded?: boolean
  version?: string
}

declare global {
  interface Window {
    fbq?: PixelFn
    _fbq?: PixelFn
  }
}

let initialized = false
const pending: Array<[string, Record<string, unknown> | undefined]> = []

function loadScript(): void {
  if (window.fbq) return

  const fbq: PixelFn = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args)
    } else {
      fbq.queue?.push(args)
    }
  }

  fbq.queue = []
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.push = fbq

  window.fbq = fbq
  window._fbq = fbq

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)
}

export function initMetaPixel(): void {
  if (initialized || typeof window === 'undefined') return
  initialized = true

  loadScript()
  window.fbq?.('init', META_PIXEL_ID)
  window.fbq?.('track', 'PageView')

  pending.splice(0).forEach(([event, params]) => {
    window.fbq?.('track', event, params)
  })
}

export function trackPixelEvent(event: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return

  if (!initialized) {
    // The visitor clicked before the deferred load ran: start it now and replay.
    pending.push([event, params])
    initMetaPixel()
    return
  }

  window.fbq?.('track', event, params)
}
